import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Paper, 
  Button, 
  IconButton, 
  TextField, 
  Box, 
  Chip, 
  Typography, 
  CircularProgress, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { formatDate } from '../../../utils/DateUtils';
import EmailTemplateEditor from './EmailTemplateEditor';
import ViewTemplateModal from './ViewTemplateModal';
import CreateTemplateModal from './CreateTemplateModal';
import EmailTemplateService from '../../../services/EmailTemplateService';
import { getEmailTemplateCategoryLabel } from '../../../utils/Enums';
import dayjs from 'dayjs';

const TemplatesTab = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorMode, setEditorMode] = useState('edit');
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const searchTimeoutRef = useRef(null);

  // Cargar templates del backend (incluye sistema y personalizados)
  const loadTemplates = useCallback(async (page, pageSize, search) => {
    try {
      setLoading(true);
      setError(null);
      const response = await EmailTemplateService.getTemplates(
        page,
        pageSize,
        search || ''
      );
      
      // El backend puede devolver {data: [...], total: number} o directamente un array
      const templates = response.data || response;
      const templatesArray = Array.isArray(templates) ? templates : [];
      
      // Log para debug: verificar isSystem
      console.log('[TemplatesTab] Templates loaded:', templatesArray.length);
      if (templatesArray.length > 0) {
        console.log('[TemplatesTab] Sample template:', {
          id: templatesArray[0].id,
          name: templatesArray[0].name,
          isSystem: templatesArray[0].isSystem
        });
      }
      
      setAllTemplates(templatesArray);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(t('error_loading_templates') || 'Error al cargar las plantillas');
      setAllTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Separar templates del sistema y personalizados
  const systemTemplates = React.useMemo(() => {
    return allTemplates.filter(t => t.isSystem === true);
  }, [allTemplates]);

  const customTemplates = React.useMemo(() => {
    return allTemplates.filter(t => !t.isSystem);
  }, [allTemplates]);

  // Cargar templates al montar y cuando cambia la paginación
  useEffect(() => {
    loadTemplates(paginationModel.page + 1, paginationModel.pageSize, searchTerm);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [paginationModel.page, paginationModel.pageSize, searchTerm, loadTemplates]);

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      loadTemplates(1, paginationModel.pageSize, newSearchTerm);
    }, 500);
  };

  // Función para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
    loadTemplates(newModel.page + 1, newModel.pageSize, searchTerm);
  };

  // Mostrar todas las plantillas (sistema y custom)
  const templates = allTemplates;

  const columns = [
    {
      field: 'name',
      headerName: t('name'),
      flex: 3,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const isSystem = params.row.isSystem === true || params.row.isSystem === 'true' || params.row.isSystem === 1;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left', gap: 1, height: '100%' }}>
            <Typography 
              variant="body2" 
              sx={{ fontWeight: isSystem ? 'bold' : 'normal' }}
            >
              {params.value}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'subject',
      headerName: t('subject'),
      flex: 3,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'category',
      headerName: t('category'),
      flex: 1.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip 
          label={getEmailTemplateCategoryLabel(params.value)} 
          size="small" 
          color="primary" 
          variant="outlined" 
        />
      )
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1.5,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isSystem = params.row.isSystem === true || params.row.isSystem === 'true' || params.row.isSystem === 1;
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, height: '100%' }}>
            {!isSystem && (
              <IconButton 
                size="small" 
                title={t('edit')}
                onClick={() => {
                  setSelectedTemplate(params.row);
                  setEditorMode('edit');
                  setIsEditorOpen(true);
                }}
                sx={{
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  minHeight: 40,
                  maxWidth: 40,
                  maxHeight: 40,
                  padding: 0,
                  borderRadius: '50%',
                  '&:hover': {
                    borderRadius: '50%'
                  }
                }}
              >
                {getIcon('edit')}
              </IconButton>
            )}
            <IconButton 
              size="small" 
              title={t('view')}
              onClick={() => {
                setSelectedTemplate(params.row);
                setIsViewModalOpen(true);
              }}
              sx={{
                width: 40,
                height: 40,
                minWidth: 40,
                minHeight: 40,
                maxWidth: 40,
                maxHeight: 40,
                padding: 0,
                borderRadius: '50%',
                '&:hover': {
                  borderRadius: '50%'
                }
              }}
            >
              {getIcon('eye')}
            </IconButton>
            {!isSystem && (
              <IconButton 
                size="small" 
                title={t('delete')} 
                color="error"
                onClick={() => {
                  setTemplateToDelete(params.row);
                  setIsDeleteDialogOpen(true);
                }}
                sx={{
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  minHeight: 40,
                  maxWidth: 40,
                  maxHeight: 40,
                  padding: 0,
                  borderRadius: '50%',
                  '&:hover': {
                    borderRadius: '50%'
                  }
                }}
              >
                {getIcon('trash')}
              </IconButton>
            )}
          </Box>
        );
      }
    }
  ];

  const handleCreateTemplate = () => {
    setIsCreateModalOpen(true);
  };

  const handleSaveNewTemplate = async (templateData) => {
    try {
      setLoading(true);
      await EmailTemplateService.createTemplate(templateData);
      setIsCreateModalOpen(false);
      await loadTemplates(); // Recargar la lista
    } catch (err) {
      console.error('Error creating template:', err);
      setError(t('error_creating_template') || 'Error al crear la plantilla');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (templateData) => {
    if (!selectedTemplate || !selectedTemplate.id) return;
    
    try {
      setLoading(true);
      await EmailTemplateService.updateTemplate(selectedTemplate.id, templateData);
      setIsEditorOpen(false);
      setSelectedTemplate(null);
      await loadTemplates(); // Recargar la lista
    } catch (err) {
      console.error('Error updating template:', err);
      setError(t('error_updating_template') || 'Error al actualizar la plantilla');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete || !templateToDelete.id) return;
    
    try {
      setLoading(true);
      await EmailTemplateService.deleteTemplate(templateToDelete.id);
      setIsDeleteDialogOpen(false);
      setTemplateToDelete(null);
      await loadTemplates(); // Recargar la lista
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(t('error_deleting_template') || 'Error al eliminar la plantilla');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ padding: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <TextField
          label={t('search')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ width: 300 }}
          placeholder={t('search_templates')}
        />
        <Button 
          variant="outlined" 
          startIcon={getIcon('add')} 
          onClick={handleCreateTemplate}
          disabled={loading}
        >
          {t('create_template')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && customTemplates.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1000 }}>
            <DataGrid
              rows={templates}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              pageSizeOptions={[10, 25, 50]}
              getRowId={(row) => row.id}
              autoHeight
              disableRowSelectionOnClick
              loading={loading}
            />
          </Box>
        </Box>
      )}

      <CreateTemplateModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleSaveNewTemplate}
        allTemplates={allTemplates}
      />

      <EmailTemplateEditor
        open={isEditorOpen}
        onClose={() => {
          setIsEditorOpen(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        onSave={handleSaveTemplate}
        mode={editorMode}
      />

      <ViewTemplateModal
        open={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
      />

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setTemplateToDelete(null);
        }}
      >
        <DialogTitle>{t('confirm_delete') || 'Confirmar eliminación'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('confirm_delete_template_message', { name: templateToDelete?.name }) || `¿Estás seguro de que deseas eliminar la plantilla "${templateToDelete?.name}"? Esta acción no se puede deshacer.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setIsDeleteDialogOpen(false);
              setTemplateToDelete(null);
            }}
            variant="outlined"
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleDeleteTemplate} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TemplatesTab;

